import React, { useCallback, useEffect, useRef, useState } from "react";
import RestaurantContractAcceptance from "./RestaurantContractAcceptance";
import RestaurantContractReceipts from "./RestaurantContractReceipts";
import RestaurantLifecyclePanel from "./RestaurantLifecyclePanel";
import { acceptExistingRestaurantContract, getRestaurantContractRequirement } from "../../services/supabase/restaurantContractService";
import { CONTRACT_SCOPE_LABELS, createContractRequestId, getContractRegistrationError } from "../../services/local/restaurantContract";

export default function RestaurantContractPriority({ companyId, children, onSignOut, onWorkspaceChanged }) {
  const [exitPanel, setExitPanel] = useState(false);
  const [state, setState] = useState(null);
  const [party, setParty] = useState({});
  const [accepted, setAccepted] = useState(false);
  const [authority, setAuthority] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [receipts, setReceipts] = useState(false);
  const request = useRef(null);
  const generation = useRef(0);
  const current = useRef(null);
  const submitting = useRef(false);
  const load = useCallback(async () => {
    if (submitting.current) return;
    const run = ++generation.current;
    try {
      const next = await getRestaurantContractRequirement(companyId);
      if (run !== generation.current) return;
      const previous = current.current;
      const changed = !previous || previous.document?.id !== next.document?.id ||
        previous.document?.contentSha256 !== next.document?.contentSha256 || previous.scopeSha256 !== next.scopeSha256 ||
        previous.email !== next.email || previous.canAccept !== next.canAccept;
      if (changed) {
        setParty(next.party || {}); setAccepted(false); setAuthority(false); request.current = null;
      }
      current.current = next; setState(next); setLoadError("");
    } catch (failure) {
      if (run === generation.current) setLoadError(failure.message);
    }
  }, [companyId]);
  useEffect(() => {
    load();
    const recheck = () => { if (!document.hidden) load(); };
    const timer = setInterval(recheck, 60000);
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      generation.current += 1;
      clearInterval(timer); window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [load]);
  const changeParty = (key, value) => {
    setParty((previous) => ({ ...previous, [key]: value }));
    setAccepted(false); setAuthority(false); request.current = null; setError("");
  };
  async function submit(event) {
    event.preventDefault();
    if (submitting.current || !state?.canAccept || loadError) return;
    const validation = getContractRegistrationError({ ...party, branchAddress: "Alcance verificado por el servidor" }, accepted, authority);
    if (validation) { setError(validation); return; }
    submitting.current = true; setBusy(true); setError(""); generation.current += 1;
    try {
      request.current ||= createContractRequestId();
      await acceptExistingRestaurantContract(companyId, {
        requestId: request.current, versionId: state.document.id, contentSha256: state.document.contentSha256,
        scopeSha256: state.scopeSha256, party, accepted, authority,
      });
      // Re-read the server: a local flag never substitutes for a persisted acceptance.
      submitting.current = false;
      await load();
    } catch (failure) { setError(failure.message); }
    finally { submitting.current = false; setBusy(false); }
  }
  if (!state || loadError) return <main className="restaurant-contract-priority">
    <h1>Contrato del restaurante</h1>
    {loadError ? <><p role="alert">{loadError}</p><button type="button" onClick={load}>Reintentar</button></>
      : <p role="status">Verificando las condiciones de tu cuenta...</p>}
    <button type="button" onClick={onSignOut}>Salir</button>
  </main>;
  if (!state.required) return children;
  if (!state.canAccept) return <>
    <div className="restaurant-contract-priority__notice" role="status">El titular de la cuenta tiene un contrato pendiente de revisar y aceptar.</div>
    {children}
  </>;
  if (exitPanel) return <main className="restaurant-contract-priority">
    <button type="button" onClick={() => setExitPanel(false)}>Volver al contrato</button>
    <RestaurantLifecyclePanel companyId={companyId} showContract onSignOut={onSignOut} onChanged={onWorkspaceChanged} />
  </main>;
  return <main className="restaurant-contract-priority">
    <h1>Revisa y acepta el contrato de tu restaurante</h1>
    <p>Esta aceptacion se registra desde hoy. No modifica retroactivamente tus operaciones ni contratos anteriores.</p>
    <form onSubmit={submit}>
      <fieldset disabled={busy} className="restaurant-contract-priority__identity">
        {[["legalName", "Nombre o razon social"], ["ownerFullName", "Nombre del representante"],
          ["ownerDocumentId", "Documento del representante"]].map(([key, label]) => <label key={key}>{label}
          <input required maxLength={500} value={party[key] || ""} onChange={(event) => changeParty(key, event.target.value)} />
        </label>)}
      </fieldset>
      <h2>Sucursales y modulos incluidos</h2>
      <ul>{state.scope.branches.map((branch) => <li key={branch.branchId}>
        <strong>{branch.branchName}</strong> | {branch.branchAddress || "Domicilio no registrado"}
        <p>{branch.serviceMode === "kiosk" ? "Autoservicio" : "Restaurante"}.
          {" "}{CONTRACT_SCOPE_LABELS.filter(([key]) => branch[key]).map(([, label]) => label).join(", ")}</p>
      </li>)}</ul>
      <RestaurantContractAcceptance document={state.document} party={party} onPartyChange={changeParty}
        accepted={accepted} authority={authority} onAcceptedChange={setAccepted} onAuthorityChange={setAuthority}
        disabled={busy} email={state.email || "Pendiente de verificar"} />
      {!state.email && <p role="alert">Confirma el correo de tu cuenta para poder aceptar.</p>}
      {error && <p role="alert">{error}</p>}
      <div className="restaurant-contract-priority__actions">
        <button type="submit" disabled={busy || !accepted || !authority || !state.email}>
          {busy ? "Registrando aceptacion..." : "Aceptar contrato y continuar"}
        </button>
        <button type="button" disabled={busy} onClick={() => load()}>Volver a comprobar</button>
        <button type="button" disabled={busy} onClick={() => setReceipts(!receipts)}>Mis contratos anteriores</button>
        <button type="button" disabled={busy} onClick={onSignOut}>Salir sin aceptar</button>
        <button type="button" disabled={busy} onClick={() => setExitPanel(true)}>Baja y descarga de datos</button>
        <a href="mailto:mozzoappenterprise@gmail.com?subject=Consulta%20sobre%20el%20contrato">Consultar a soporte</a>
      </div>
    </form>
    {receipts && <RestaurantContractReceipts companyId={companyId} />}
  </main>;
}
